import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { Controller, useForm } from 'react-hook-form';
import media from '../style/media';
import { Flex } from '../components/shared/Flex';
import { useCreatePatientMutation } from 'services/patientApi';

// Define the mandated colors
import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard, Grid } from '../style/layout';
import { User, Mail, Phone, Calendar, MapPin, Globe, Map, Trash2, CheckCircle } from 'lucide-react';

const FormField = styled.div<{ gridColumn?: string }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  ${props => props.gridColumn && `grid-column: ${props.gridColumn};`}
`;

const FormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  padding-left: 0.25rem;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 1rem;
    color: #94a3b8;
    pointer-events: none;
    z-index: 10;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.875rem 0.875rem 0.875rem 3rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  color: #0f172a;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.875rem 0.875rem 0.875rem 3rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  color: #0f172a;
  appearance: none;
  cursor: pointer;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }
`;

const ErrorMsg = styled.div`
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  padding-left: 0.25rem;
`;

const ButtonRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.875rem 1.75rem;
  border-radius: 16px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.variant === 'primary'
    ? `
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(102, 126, 234, 0.35);
        opacity: 0.95;
      }
    `
    : `
      background: #f1f5f9;
      color: #475569;
      border: 1px solid #e2e8f0;
      &:hover {
        background: #e2e8f0;
        color: #0f172a;
      }
    `}

  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  age: number;
  dob?: string;
  gender?: string;
  address: string;
  city?: string;
  state?: string;
  country_code?: string;
}

export default function PatientFormLayout() {
  const nav = useNavigate();
  const cameras = useCameras();
  const [createPatient, { isLoading }] = useCreatePatientMutation();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const toggleSettings = () => setIsSettingsOpen(prev => !prev);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      age: 0,
      gender: '',
      address: '',
      city: '',
      state: '',
      country_code: 'IN',
      dob: new Date().toISOString().split('T')[0]
    }
  });

  const onSubmit = async (data: FormData) => {
    try {
      const formatted = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        phone: data.phone.trim(),
        age: data.age,
        dob: data.dob || '',
        gender_type: data.gender || 'F',
        address: data.address.trim(),
        city: data.city || '',
        state: data.state || '',
        country_code: data.country_code || 'IN'
      };

      const result = await createPatient(formatted).unwrap();

      if (result.success) {
        localStorage.setItem('patient_id', result?.data?.patient?.id);
        nav('/health-assessment');
      }
    } catch (error) {
      console.error('Error creating patient:', error);
    }
  };

  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />

      <SettingsBars
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        cameras={cameras}
        isLicenseValid={true}
      />

      <ContentWrapper>
        <GlassCard>
          <Header>
            <Title>Patient Information</Title>
            <Subtitle>Enter contact details for report generation</Subtitle>
          </Header>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid>
              <FormField>
                <FormLabel>First Name</FormLabel>
                <InputWrapper>
                  <User size={18} />
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: 'First name is required' }}
                    render={({ field }) => <FormInput {...field} placeholder="Enter first name" />}
                  />
                </InputWrapper>
                {errors.firstName && <ErrorMsg>{errors.firstName.message}</ErrorMsg>}
              </FormField>

              <FormField>
                <FormLabel>Last Name</FormLabel>
                <InputWrapper>
                  <User size={18} />
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: 'Last name is required' }}
                    render={({ field }) => <FormInput {...field} placeholder="Enter last name" />}
                  />
                </InputWrapper>
                {errors.lastName && <ErrorMsg>{errors.lastName.message}</ErrorMsg>}
              </FormField>

              <FormField gridColumn="1 / -1">
                <FormLabel>Email Address</FormLabel>
                <InputWrapper>
                  <Mail size={18} />
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Invalid email address'
                      }
                    }}
                    render={({ field }) => <FormInput {...field} type="email" placeholder="example@domain.com" />}
                  />
                </InputWrapper>
                {errors.email && <ErrorMsg>{errors.email.message}</ErrorMsg>}
              </FormField>

              <FormField>
                <FormLabel>Phone Number</FormLabel>
                <InputWrapper>
                  <Phone size={18} />
                  <Controller
                    name="phone"
                    control={control}
                    rules={{ required: 'Phone number is required' }}
                    render={({ field }) => <FormInput {...field} placeholder="+1 234 567 8900" />}
                  />
                </InputWrapper>
                {errors.phone && <ErrorMsg>{errors.phone.message}</ErrorMsg>}
              </FormField>

              <FormField>
                <FormLabel>Date of Birth</FormLabel>
                <InputWrapper>
                  <Calendar size={18} />
                  <Controller
                    name="dob"
                    control={control}
                    rules={{ required: 'Date of Birth is required' }}
                    render={({ field }) => <FormInput {...field} type="date" />}
                  />
                </InputWrapper>
                {errors.dob && <ErrorMsg>{errors.dob.message}</ErrorMsg>}
              </FormField>

              <FormField>
                <FormLabel>Gender</FormLabel>
                <InputWrapper>
                  <User size={18} />
                  <Controller
                    name="gender"
                    control={control}
                    rules={{ required: 'Gender is required' }}
                    render={({ field }) => (
                      <FormSelect {...field}>
                        <option value="">Select Gender</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="O">Other</option>
                      </FormSelect>
                    )}
                  />
                </InputWrapper>
                {errors.gender && <ErrorMsg>{errors.gender.message}</ErrorMsg>}
              </FormField>

              <FormField gridColumn="1 / -1">
                <FormLabel>Residential Address</FormLabel>
                <InputWrapper>
                  <MapPin size={18} />
                  <Controller
                    name="address"
                    control={control}
                    rules={{ required: 'Address is required' }}
                    render={({ field }) => <FormInput {...field} placeholder="Street, Building, Apartment" />}
                  />
                </InputWrapper>
                {errors.address && <ErrorMsg>{errors.address.message}</ErrorMsg>}
              </FormField>

              <FormField>
                <FormLabel>City</FormLabel>
                <InputWrapper>
                  <Map size={18} />
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => <FormInput {...field} placeholder="Enter city" />}
                  />
                </InputWrapper>
              </FormField>

              <FormField>
                <FormLabel>State / Province</FormLabel>
                <InputWrapper>
                  <Globe size={18} />
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => <FormInput {...field} placeholder="Enter state" />}
                  />
                </InputWrapper>
              </FormField>
            </Grid>

            <ButtonRow>
              <ActionButton type="button" onClick={() => reset()}>
                <Trash2 size={18} />
                <span>Clear</span>
              </ActionButton>

              <ActionButton variant="primary" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <span>Saving...</span>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    <span>Proceed & Save</span>
                  </>
                )}
              </ActionButton>
            </ButtonRow>
          </form>
        </GlassCard>
      </ContentWrapper>
    </PageContainer>
  );
}